import React, { useEffect, useState } from 'react';
import { ChevronDown, Phone, MapPin, Flame, Fish, Heart, Beef } from 'lucide-react';
import { restaurantInfo, images } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const featuresMap = {
    it: [
      { icon: Flame, text: "Pizza al forno a legna" },
      { icon: Fish, text: "Specialità di pesce" },
      { icon: Beef, text: "Specialità di carne" },
      { icon: Heart, text: "Cucina familiare" }
    ],
    de: [
      { icon: Flame, text: "Holzofenpizza" },
      { icon: Fish, text: "Fischspezialitäten" },
      { icon: Beef, text: "Fleischspezialitäten" },
      { icon: Heart, text: "Familienküche" }
    ],
    en: [
      { icon: Flame, text: "Wood-fired Pizza" },
      { icon: Fish, text: "Fish Specialties" },
      { icon: Beef, text: "Meat Specialties" },
      { icon: Heart, text: "Family Cooking" }
    ]
  };

  const features = featuresMap[language] || featuresMap.it;

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image - Different for mobile and desktop */}
      <div className="absolute inset-0">
        {/* Mobile background */}
        <img
          src={images.heroMobile}
          alt="Ristorante Mezzaluna"
          className="w-full h-full object-cover sm:hidden bg-[#1a0f0a]"
        />
        {/* Desktop background */}
        <img
          src={images.hero}
          alt="Ristorante Mezzaluna"
          className="w-full h-full object-cover hidden sm:block"
        />
        {/* Warm overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a0f0a]/70 via-[#2a1810]/60 to-[#1a0f0a]/80"></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 container mx-auto px-4 pt-32 pb-20">
        <div className="flex flex-col items-center text-center">
          
          {/* Location badge */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 bg-[#c9a227]/20 backdrop-blur-sm border border-[#c9a227]/40 px-5 py-2 rounded-full mb-8">
              <MapPin size={16} className="text-[#c9a227]" />
              <span className="text-sm text-[#e8d48b] font-medium tracking-wide">Lavertezzo Piano</span>
            </div>
          </div>

          {/* Main title - warm and welcoming */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-3 drop-shadow-lg">
              Mezzaluna
            </h1>
            <p className="text-xl md:text-2xl text-[#e8d48b] font-elegant italic mb-2">
              {restaurantInfo.subtitle[language]}
            </p>
          </div>

          {/* Warm tagline */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <p className="text-lg md:text-xl text-white/90 font-medium mb-2 max-w-xl">
              {restaurantInfo.tagline[language]}
            </p>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-lg">
              {restaurantInfo.description[language]}
            </p>
          </div>

          {/* Feature tags */}
          <div className={`flex flex-wrap justify-center gap-3 mb-10 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <span 
                  key={index}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full text-white text-sm hover:bg-[#c9a227]/20 hover:border-[#c9a227]/50 transition-all duration-300"
                >
                  <Icon size={16} className="text-[#c9a227]" />
                  {feature.text}
                </span>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <a
              href="#contatti"
              className="group inline-flex items-center justify-center gap-3 bg-[#c9a227] text-[#2a1810] px-8 py-4 rounded-full font-bold tracking-wide hover:bg-[#e8d48b] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              {language === 'it' ? 'Prenota un tavolo' : language === 'de' ? 'Tisch reservieren' : 'Book a table'}
              <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
            </a>
            <a
              href={`tel:${restaurantInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-bold tracking-wide border border-white/30 hover:bg-white/20 hover:border-white/50 transition-all duration-300"
            >
              <Phone size={18} />
              {language === 'it' ? 'Chiama ora' : language === 'de' ? 'Jetzt anrufen' : 'Call now'}
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-xs text-white/50 font-medium tracking-widest uppercase">
            {language === 'it' ? 'Scopri di più' : language === 'de' ? 'Mehr erfahren' : 'Learn more'}
          </span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#c9a227] rounded-full animate-scroll-indicator"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
